import { useMemo, useState, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { fetchUsers } from '@/lib/admin/users';
import type { AdminUserListItem, ChurnRisk } from '@/types/admin';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/cn';

function ChurnBadge({ risk }: { risk: ChurnRisk }) {
  const { t } = useTranslation();
  const styles: Record<ChurnRisk, string> = {
    LOW: 'bg-emerald-100 text-emerald-700',
    MEDIUM: 'bg-amber-100 text-amber-700',
    HIGH: 'bg-rose-100 text-rose-700',
  };
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold uppercase', styles[risk])}>
      {t(`admin.churnRisk.${risk.toLowerCase()}`)}
    </span>
  );
}

export default function UsersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [debounced, setDebounced] = useState(search);
  const plan = searchParams.get('plan') ?? '';
  const status = searchParams.get('status') ?? '';
  const churnRiskFilter = searchParams.get('churnRisk') ?? '';
  const hasIntent = searchParams.get('hasIntent') === '1';
  const page = Number(searchParams.get('page')) || 1;
  const perPage = 25;
  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  const sortParam = useMemo(() => {
    if (!sorting.length) return undefined;
    const s = sorting[0];
    return `${s.id}:${s.desc ? 'desc' : 'asc'}`;
  }, [sorting]);

  const { data, isFetching } = useQuery({
    queryKey: ['admin', 'users', { q: debounced, plan, status, hasIntent, page, sortParam }],
    queryFn: () =>
      fetchUsers({
        q: debounced || undefined,
        plan: plan || undefined,
        status: status || undefined,
        hasIntent: hasIntent || undefined,
        page,
        perPage,
        sort: sortParam,
      }),
    placeholderData: keepPreviousData,
  });

  const items = useMemo(() => {
    const list = data?.items ?? [];
    if (!churnRiskFilter) return list;
    return list.filter((u) => u.churnRisk === churnRiskFilter);
  }, [data?.items, churnRiskFilter]);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  const columnHelper = createColumnHelper<AdminUserListItem>();
  const columns = useMemo(
    () => [
      columnHelper.accessor((u) => `${u.firstName} ${u.lastName}`, {
        id: 'name',
        header: t('admin.columns.user'),
        cell: (info) => (
          <div>
            <div className="font-medium text-gray-900">{info.getValue() || '—'}</div>
            <div className="text-xs text-gray-500">{info.row.original.email}</div>
          </div>
        ),
      }),
      columnHelper.accessor((u) => u.subscription?.plan ?? '', {
        id: 'plan',
        header: t('admin.columns.plan'),
        cell: (info) => <span className="text-sm capitalize">{info.getValue() || '—'}</span>,
      }),
      columnHelper.accessor((u) => u.subscription?.status ?? '', {
        id: 'status',
        header: t('admin.columns.subStatus'),
        cell: (info) => <span className="text-sm capitalize">{info.getValue() || '—'}</span>,
      }),
      columnHelper.accessor('lastLoginAt', {
        header: t('admin.columns.lastLogin'),
        cell: (info) => (
          <span className="text-sm text-gray-700">
            {info.getValue() ? new Date(info.getValue() as string).toLocaleString() : '—'}
          </span>
        ),
      }),
      columnHelper.accessor('engagementScore', {
        header: t('admin.columns.engagement'),
        cell: (info) => <span className="text-sm font-semibold">{info.getValue()}</span>,
      }),
      columnHelper.accessor('churnRisk', {
        header: t('admin.columns.churn'),
        cell: (info) => <ChurnBadge risk={info.getValue()} />,
      }),
    ],
    [columnHelper, t],
  );

  const table = useReactTable({
    data: items,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">{t('admin.nav.users')}</h1>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder={t('common.search')}
            className="min-w-[240px] flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setParam('q', e.target.value);
            }}
          />
          <select
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
            value={plan}
            onChange={(e) => setParam('plan', e.target.value)}
          >
            <option value="">{t('admin.filters.allPlans')}</option>
            <option value="free">free</option>
            <option value="starter">starter</option>
            <option value="professional">professional</option>
            <option value="enterprise">enterprise</option>
          </select>
          <select
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
            value={status}
            onChange={(e) => setParam('status', e.target.value)}
          >
            <option value="">{t('admin.filters.allStatuses')}</option>
            <option value="active">active</option>
            <option value="trialing">trialing</option>
            <option value="past_due">past_due</option>
            <option value="canceled">canceled</option>
          </select>
          <select
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
            value={churnRiskFilter}
            onChange={(e) => setParam('churnRisk', e.target.value)}
          >
            <option value="">{t('admin.filters.allRisks')}</option>
            <option value="LOW">{t('admin.churnRisk.low')}</option>
            <option value="MEDIUM">{t('admin.churnRisk.medium')}</option>
            <option value="HIGH">{t('admin.churnRisk.high')}</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={hasIntent}
              onChange={(e) => setParam('hasIntent', e.target.checked ? '1' : '')}
            />
            {t('admin.filters.hasIntent')}
          </label>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th
                    key={h.id}
                    className="cursor-pointer px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                    onClick={h.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center gap-1">
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {h.column.getIsSorted() === 'asc' && <ChevronUpIcon className="h-3 w-3" />}
                      {h.column.getIsSorted() === 'desc' && <ChevronDownIcon className="h-3 w-3" />}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {table.getRowModel().rows.length === 0 && !isFetching && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-gray-500">
                  {t('common.noResults')}
                </td>
              </tr>
            )}
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/admin/users/${row.original.id}`)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-3 text-sm">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
          <span>
            {t('common.showing')} {items.length} {t('common.of')} {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              className="rounded border border-gray-200 px-3 py-1 disabled:opacity-50"
              disabled={page <= 1}
              onClick={() => setParam('page', String(page - 1))}
            >
              ‹
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              className="rounded border border-gray-200 px-3 py-1 disabled:opacity-50"
              disabled={page >= totalPages}
              onClick={() => setParam('page', String(page + 1))}
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
